import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import DrawIcon from "@mui/icons-material/Draw";
import LinkIcon from "@mui/icons-material/Link";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { format } from "date-fns";
import ConfirmDialog from "components/common/ConfirmDialog";
import {
  subscribeToBaaRequest,
  subscribeToAuditLog,
  updateBaaRequestNotes,
  initiateBAASigning,
  regenerateBAASigningUrl,
  getBAAdocumentUrl,
} from "services/baaAdmin";
import { statusColor, statusLabel } from "features/baa/baaStatus";

const toDate = (ts) => {
  if (!ts) return null;
  return typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
};
const formatDateTime = (ts) => {
  const d = toDate(ts);
  return d ? format(d, "MMM d, yyyy h:mm a") : "—";
};

const Field = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography>{value || "—"}</Typography>
  </Grid>
);

export default function BAARequestDetail({ requestId }) {
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [notes, setNotes] = useState("");
  const [notesDirty, setNotesDirty] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [initiating, setInitiating] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(null);
  const [signingLink, setSigningLink] = useState(null);

  useEffect(() => {
    let loaded = false;
    const unsubRequest = subscribeToBaaRequest(
      requestId,
      (doc) => {
        setRequest(doc);
        if (!loaded) {
          setNotes(doc?.notes || "");
          loaded = true;
        }
        setLoading(false);
      },
      (err) => {
        setError(err.message || "Failed to load request.");
        setLoading(false);
      }
    );
    const unsubAudit = subscribeToAuditLog(
      requestId,
      (entries) => setAuditLog(entries),
      () => {}
    );
    return () => {
      unsubRequest();
      unsubAudit();
    };
  }, [requestId]);

  const canInitiate = request && request.status === "pending";
  const canRegenerate = request && request.status === "sent_for_signature";
  const canDownload =
    request &&
    request.status === "fully_executed" &&
    Boolean(request.executedDocumentStoragePath);

  const handleNotesBlur = async () => {
    if (!notesDirty) return;
    try {
      await updateBaaRequestNotes(requestId, notes);
      setNotesDirty(false);
    } catch (err) {
      setActionError(err.message || "Failed to save notes.");
    }
  };

  const handleInitiate = async () => {
    setActionError(null);
    setActionSuccess(null);
    setInitiating(true);
    try {
      const { signingUrl } = await initiateBAASigning(requestId);
      setSigningLink(signingUrl || null);
      setActionSuccess(
        signingUrl
          ? "Signing started. Share the in-browser signing link below with the signatory."
          : "Signing started."
      );
      setConfirmOpen(false);
    } catch (err) {
      setActionError(err.message || "Failed to initiate signing.");
    } finally {
      setInitiating(false);
    }
  };

  const handleRegenerate = async () => {
    setActionError(null);
    setActionSuccess(null);
    setRegenerating(true);
    try {
      const { url } = await regenerateBAASigningUrl(requestId);
      setSigningLink(url || null);
      setActionSuccess("Fresh signing link generated.");
    } catch (err) {
      setActionError(err.message || "Failed to generate a signing link.");
    } finally {
      setRegenerating(false);
    }
  };

  const handleCopyLink = async () => {
    if (!signingLink) return;
    try {
      await navigator.clipboard.writeText(signingLink);
      setActionSuccess("Signing link copied to clipboard.");
    } catch {
      setActionError("Couldn't copy automatically — select and copy the link below.");
    }
  };

  const handleDownload = async () => {
    setActionError(null);
    setDownloading(true);
    try {
      const { url } = await getBAAdocumentUrl(requestId);
      if (url) window.open(url, "_blank", "noopener");
    } catch (err) {
      setActionError(err.message || "Failed to generate download link.");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!request) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/admin/baa/requests")}>
          Back to requests
        </Button>
        <Alert severity="warning" sx={{ mt: 2 }}>
          {error || "Request not found."}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/admin/baa/requests")}
        sx={{ mb: 2 }}
      >
        Back to requests
      </Button>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Typography variant="h4" component="h1">
          {request.institutionName}
        </Typography>
        <Chip label={statusLabel(request.status)} color={statusColor(request.status)} />
      </Stack>

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}
      {actionSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setActionSuccess(null)}>
          {actionSuccess}
        </Alert>
      )}

      <Stack direction="row" spacing={1.5} sx={{ mb: 3 }} flexWrap="wrap">
        {canInitiate && (
          <Button
            variant="contained"
            startIcon={<DrawIcon />}
            onClick={() => setConfirmOpen(true)}
          >
            Initiate signing
          </Button>
        )}
        {canRegenerate && (
          <LoadingButton
            variant="outlined"
            startIcon={<LinkIcon />}
            loading={regenerating}
            onClick={handleRegenerate}
          >
            Get signing link
          </LoadingButton>
        )}
        {canDownload && (
          <LoadingButton
            variant="outlined"
            startIcon={<DownloadIcon />}
            loading={downloading}
            onClick={handleDownload}
          >
            Download executed BAA
          </LoadingButton>
        )}
      </Stack>

      {signingLink && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle2">In-browser signing link</Typography>
            <Typography variant="caption" color="text.secondary">
              Single-use and short-lived — send it to the signatory now. If it
              expires, generate a fresh one with “Get signing link”.
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                value={signingLink}
                size="small"
                fullWidth
                InputProps={{ readOnly: true }}
              />
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyLink}
                sx={{ flexShrink: 0 }}
              >
                Copy
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Request details
        </Typography>
        <Grid container spacing={2}>
          <Field label="Institution" value={request.institutionName} />
          <Field label="Institution type" value={request.institutionType} />
          <Field label="Signatory" value={request.signatoryName} />
          <Field label="Signatory title" value={request.signatoryTitle} />
          <Field label="Signatory email" value={request.signatoryEmail} />
          <Field label="Submitted" value={formatDateTime(request.submittedAt)} />
          <Field label="Submitter IP" value={request.submitterIp} />
          <Field label="DocuSign envelope" value={request.esignEnvelopeId} />
          {request.signingUrl && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Signing link
              </Typography>
              <Typography noWrap>
                <Link href={request.signingUrl} target="_blank" rel="noopener">
                  {request.signingUrl}
                </Link>
              </Typography>
            </Grid>
          )}
          <Field label="Executed at" value={formatDateTime(request.executedAt)} />
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Internal notes
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          placeholder="Notes visible to the compliance team only"
          value={notes}
          onChange={(e) => {
            setNotes(e.target.value);
            setNotesDirty(true);
          }}
          onBlur={handleNotesBlur}
        />
      </Paper>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Audit log
        </Typography>
        {auditLog.length === 0 ? (
          <Typography color="text.secondary">No audit entries yet.</Typography>
        ) : (
          <Stack spacing={2}>
            {auditLog.map((entry, idx) => (
              <Box key={entry.id || idx}>
                <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
                  <Typography variant="subtitle2">
                    {entry.action.replace(/_/g, " ")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDateTime(entry.timestamp)}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {entry.actorEmail || entry.actorUid || "system"}
                  {entry.previousStatus || entry.newStatus
                    ? ` — ${entry.previousStatus || "—"} → ${entry.newStatus || "—"}`
                    : ""}
                </Typography>
                {idx < auditLog.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Initiate signing?"
        message={`This will create a DocuSign envelope for ${request.signatoryName} (${request.signatoryEmail}) and generate an in-browser signing link to share with them. VoxPont's authorized signatory will countersign afterward.`}
        confirmLabel="Initiate signing"
        onConfirm={handleInitiate}
        loading={initiating}
      />
    </Container>
  );
}
